import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminApi, setToken, formatApiError } from "@/lib/admin";
import { API } from "@/lib/site";

const SAMPLE = `[
  {
    "vehicle_type": "Cars / Vans",
    "make": "Audi",
    "model": "A3",
    "generation": "8V (2012–2020)",
    "engine": "2.0 TDI 150",
    "engine_size": "1968 cc",
    "fuel": "Diesel",
    "year_from": "2012",
    "year_to": "2020",
    "original_bhp": 150,
    "original_nm": 340,
    "stage1_bhp": 190,
    "stage1_nm": 400,
    "source": "Regal Tuning verified calibration"
  }
]`;

const inputCls = "w-full border border-line bg-white px-4 py-2.5 text-sm text-ink outline-none focus:border-regal-amber transition-colors";

export default function AdminTuningData() {
  const [stats, setStats] = useState({ total: 0, synced_at: "", source: "" });
  const [text, setText] = useState("");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/tuning-database/filters`)
      .then((r) => setStats({ total: r.data.total, synced_at: r.data.synced_at, source: r.data.source }))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    let rows;
    try {
      rows = JSON.parse(text);
      if (!Array.isArray(rows)) throw new Error("not an array");
    } catch {
      toast.error("Invalid JSON", { description: "Paste a JSON array of vehicle rows (see format below)." });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await adminApi().post("/admin/tuning-gains/import", { rows, replace, source: "Admin panel import" });
      setResult(r.data);
      toast.success(`Imported ${r.data.imported} new, updated ${r.data.updated}`, { description: `Database now holds ${r.data.total} vehicles.` });
      setText("");
      load();
    } catch (e) {
      if (e.response?.status === 401) { setToken(null); nav("/admin/login"); }
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-surface pt-28 pb-20" data-testid="admin-tuning-data-page">
      <div className="container-regal max-w-4xl">
        <Link to="/admin" className="mb-6 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-regal-amber" data-testid="admin-td-back"><ArrowLeft size={15} /> All products</Link>
        <h1 className="mb-2 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Tuning Gains Database</h1>
        <p className="mb-8 text-sm text-neutral-400">Bulk import vehicle performance data for the public <Link to="/tuning-database" className="text-regal-amber underline-offset-4 hover:underline">Tuning Database</Link>. Rows upsert on make + model + generation + engine, so re-importing updates existing records.</p>

        <section className="border border-line bg-white p-6 sm:p-8" data-testid="admin-td-stats">
          <p className="overline-label mb-4">Current database</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div><p className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">Vehicles</p><p className="mt-1 font-display text-2xl font-extrabold text-ink" data-testid="admin-td-total">{stats.total.toLocaleString()}</p></div>
            <div><p className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">Last import</p><p className="mt-1 text-sm text-ink" data-testid="admin-td-synced">{stats.synced_at ? new Date(stats.synced_at).toLocaleString("en-GB") : "—"}</p></div>
            <div><p className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">Source</p><p className="mt-1 text-sm text-ink" data-testid="admin-td-source">{stats.source || "—"}</p></div>
          </div>
        </section>

        <section className="mt-8 border border-line bg-white p-6 sm:p-8">
          <p className="overline-label mb-4">Import rows (JSON)</p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            placeholder={SAMPLE}
            className={`${inputCls} font-mono text-xs leading-relaxed`}
            data-testid="admin-td-input"
          />
          <label className="mt-4 flex items-center gap-2.5 text-sm text-neutral-500">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="admin-td-replace" className="h-4 w-4 accent-[#0A3D8F]" />
            Replace entire database first (otherwise rows are added/updated)
          </label>
          <button onClick={run} disabled={busy || !text.trim()} data-testid="admin-td-import-button" className="mt-5 inline-flex items-center gap-2 bg-regal-amber px-10 py-3.5 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors disabled:opacity-60">
            <Upload size={15} /> {busy ? "Importing…" : "Import Data"}
          </button>
          {result && (
            <div className="mt-5 border border-line bg-surface p-5 font-mono text-xs text-neutral-500" data-testid="admin-td-result">
              <p>received: {result.received} · imported: {result.imported} · updated: {result.updated} · total: {result.total}</p>
              {result.errors?.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-regal-red" data-testid="admin-td-errors">
                  {result.errors.map((e) => <li key={e}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
        </section>

        <section className="mt-8 border border-line bg-white p-6 sm:p-8">
          <p className="overline-label mb-4">Row format</p>
          <pre className="overflow-x-auto border border-line bg-surface p-4 font-mono text-xs leading-relaxed text-neutral-500" data-testid="admin-td-format">{SAMPLE}</pre>
          <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-neutral-400">
            <li><strong className="text-ink">make, model, engine</strong> are required on every row.</li>
            <li><strong className="text-ink">vehicle_type</strong>: Cars / Vans · Trucks / HGV · Agricultural · Motorcycles / ATV · Marine (defaults to Cars / Vans).</li>
            <li><strong className="text-ink">stage1_bhp / stage1_nm</strong> are optional — leave empty until verified tuning data is available; gains are calculated automatically.</li>
            <li>Empty or invalid payloads are rejected before anything is deleted — a bad import can never wipe the database.</li>
          </ul>
        </section>
      </div>
    </main>
  );
}
